import React, { useEffect, useState } from "react";
import { Form, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import UploadFileDraggable from "../Form/UploadFileDraggable";
import { useTranslation } from "react-i18next";
import { FILE_TYPE, SIMULATION_TYPE, TARGET_MODE, UPLOAD_TYPE } from "src/constant/common";
import { useForm } from "react-hook-form";
import { useRecoilState, useRecoilValue } from "recoil";
import { alertState, uploadingFile } from "src/recoil/atoms/global";
import {
  convertFilesUploadDataToArray,
  duplicateFiles,
  filterUploadedFilesByTargetMode,
  findDuplicates,
  mapUploadedFilesToFormData,
  updateUploadMultipleState,
} from "src/utils/common";

import UploadMoreFiles from "../Form/UploadMoreFiles";
import classNames from "classnames";
import { uploadedFiles6DOFState, uploadMultiple6DOFState } from "src/recoil/atoms/flightSimulator";
import { cloneDeep } from "lodash";
import { setRecoil } from "recoil-nexus";
import batchUpload from "src/Hooks/useBatchUpload";
import { getPreSignedUrlTestAnalysis } from "src/services/upload";

export const FORM_NAME = {
  PARAMETER_JSON: "parameter_json",
  MASS_AND_SHAPE_RESPONSE: "mass_and_shape_response",
  AERO_COEFFICIENTS_ASCENT: "aero_coefficients_ascent",
  AERO_COEFFICIENTS_ASCENT_2: "aero_coefficients_ascent_2",
  AERO_COEFFICIENTS_DESCENT: "aero_coefficients_descent",
  WIND_MODEL: "wind_model",
  THRUST_PROFILE: "thrust_profile",
  TARGET_MODE: "target_mode",
  TARGET_ATTITUDE_RATE_ASCENT: "target_attitude_rate_ascent",
  TARGET_ATTITUDE_RATE_DESCENT: "target_attitude_rate_descent",
  TARGET_ATTITUDE_ASCENT: "target_attitude_ascent",
  TARGET_ATTITUDE_DESCENT: "target_attitude_descent",
};

const DEFAULT_MULTIPLE_STATE = {
  [FORM_NAME.PARAMETER_JSON]: true,
  [FORM_NAME.MASS_AND_SHAPE_RESPONSE]: true,
  [FORM_NAME.AERO_COEFFICIENTS_ASCENT]: true,
  [FORM_NAME.AERO_COEFFICIENTS_ASCENT_2]: true,
  [FORM_NAME.AERO_COEFFICIENTS_DESCENT]: true,
  [FORM_NAME.WIND_MODEL]: true,
  [FORM_NAME.THRUST_PROFILE]: true,
  [FORM_NAME.TARGET_ATTITUDE_RATE_ASCENT]: true,
  [FORM_NAME.TARGET_ATTITUDE_RATE_DESCENT]: true,
  [FORM_NAME.TARGET_ATTITUDE_ASCENT]: true,
  [FORM_NAME.TARGET_ATTITUDE_DESCENT]: true,
};

const DEFAULT_FORM_VALUES = {
  [FORM_NAME.PARAMETER_JSON]: [],
  [FORM_NAME.MASS_AND_SHAPE_RESPONSE]: [],
  [FORM_NAME.AERO_COEFFICIENTS_ASCENT]: [],
  [FORM_NAME.AERO_COEFFICIENTS_ASCENT_2]: [],
  [FORM_NAME.AERO_COEFFICIENTS_DESCENT]: [],
  [FORM_NAME.WIND_MODEL]: [],
  [FORM_NAME.THRUST_PROFILE]: [],
  [FORM_NAME.TARGET_MODE]: 1,
  [FORM_NAME.TARGET_ATTITUDE_RATE_ASCENT]: [],
  [FORM_NAME.TARGET_ATTITUDE_RATE_DESCENT]: [],
  [FORM_NAME.TARGET_ATTITUDE_ASCENT]: [],
  [FORM_NAME.TARGET_ATTITUDE_DESCENT]: [],
};

const ModalUploadFile6DOF = ({ paramsInput, toggle, setToggle, onModalSubmit }) => {
  const { t } = useTranslation("flight-simulator");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    clearErrors,
    reset,
    getValues,
    resetField,
  } = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
  });
  const [globalUploadingField, setGlobalUploadingField] = useRecoilState(uploadingFile);
  const [uploadMultiple, setUploadMultiple] = useRecoilState(uploadMultiple6DOFState);
  const uploadedFiles6DOF = useRecoilValue(uploadedFiles6DOFState);

  const targetModeValue = getValues(FORM_NAME.TARGET_MODE);
  const [targetMode, setTargetMode] = useState(targetModeValue);

  const handleFileUpload = async (fieldName, files) => {
    if (!files) {
      return;
    }

    setValue(fieldName, files);
    clearErrors(fieldName);
    const updateState = updateUploadMultipleState(getValues(), uploadMultiple);
    setUploadMultiple(updateState);
  };

  const handleModalFileDelete = (fieldName, files, fileSelected) => {
    setValue(fieldName, files);
    clearErrors(fieldName);

    const updateState = updateUploadMultipleState(getValues(), uploadMultiple);
    setUploadMultiple(updateState);
  };

  const showDuplicateFileNameErrorMessage = (hasRowDuplicated) => {
    if (hasRowDuplicated?.length > 0) {
      setRecoil(alertState, () => ({
        status: true,
        message: t("new.validate_duplicate_file_name"),
        type: "danger",
      }));
      setUploadMultiple(DEFAULT_MULTIPLE_STATE);
      reset(DEFAULT_FORM_VALUES);
      return;
    }
  };

  const removeFieldInArrayObject = (data, field) => {
    if (Array.isArray(data)) {
      data.forEach((item) => {
        delete item?.[field];
      });
    } else {
      delete data?.[field];
    }
  };

  const formDataModified = (formData) => {
    if (targetMode === TARGET_MODE.RATE) {
      formData[FORM_NAME.TARGET_ATTITUDE_ASCENT] = [];
      formData[FORM_NAME.TARGET_ATTITUDE_DESCENT] = [];
    }
    if (targetMode === TARGET_MODE.ATTITUDE) {
      formData[FORM_NAME.TARGET_ATTITUDE_RATE_ASCENT] = [];
      formData[FORM_NAME.TARGET_ATTITUDE_RATE_DESCENT] = [];
    }
    return formData;
  };

  const onSubmit = async (data) => {
    setToggle(false);

    try {
      const paramsInputClone = cloneDeep(paramsInput);
      const formData = formDataModified(cloneDeep(data));
      const allFiles = convertFilesUploadDataToArray(formData, SIMULATION_TYPE["6DOF"]);
      const allFilesClone = cloneDeep(allFiles);
      removeFieldInArrayObject(paramsInputClone, FORM_NAME.TARGET_MODE);
      removeFieldInArrayObject(allFilesClone, FORM_NAME.TARGET_MODE);

      if (!uploadedFiles6DOF || uploadedFiles6DOF.length === 0) {
        const hasRowDuplicated = findDuplicates(paramsInputClone, allFilesClone);
        if (hasRowDuplicated?.length > 0) {
          return showDuplicateFileNameErrorMessage(hasRowDuplicated);
        }

        onModalSubmit(allFiles);
        return;
      }

      const hasRowDuplicated = findDuplicates(paramsInputClone, allFilesClone);

      if (hasRowDuplicated?.length > 0) {
        return showDuplicateFileNameErrorMessage(hasRowDuplicated);
      }

      setGlobalUploadingField(true);

      const uploadedFiles6DOFModified = filterUploadedFilesByTargetMode(uploadedFiles6DOF, targetMode, (filesFiltered) => {});

      const fileContents = uploadedFiles6DOFModified.map((item) => item?.file);
      const duplicatedFiles = duplicateFiles(formData, fileContents);

      const results = await batchUpload(duplicatedFiles, 6, UPLOAD_TYPE["6DOF"], getPreSignedUrlTestAnalysis);
      const updatedData = mapUploadedFilesToFormData(allFiles, results);
      onModalSubmit(updatedData);
      reset(DEFAULT_FORM_VALUES);
      setGlobalUploadingField(false);
      setUploadMultiple(DEFAULT_MULTIPLE_STATE);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancel = () => {
    setUploadMultiple(DEFAULT_MULTIPLE_STATE);
    reset(DEFAULT_FORM_VALUES);
    setToggle(false);
  };

  const isShowUploadMoreFilesButton = (fieldName) => {
    const isMultiple = uploadMultiple[fieldName];
    const hasItems = getValues(fieldName)?.length > 0;

    return isMultiple && hasItems;
  };

  useEffect(() => {
    setTargetMode(targetModeValue);
  }, [targetModeValue]);

  return (
    <Modal centered isOpen={toggle} toggle={handleCancel} size="xl" backdrop="static">
      <ModalHeader>{t("new.input_files")}</ModalHeader>
      <ModalBody className="p-y-0 max-vh-78 overflow-auto">
        <Form className="p-x-15" onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-lg-6 p-a-16 important-p-y-0">
              <div>
                <UploadFileDraggable
                  classGroup={classNames({
                    "mb-0": isShowUploadMoreFilesButton(FORM_NAME.PARAMETER_JSON),
                  })}
                  name={FORM_NAME.PARAMETER_JSON}
                  setValue={(files) => handleFileUpload(FORM_NAME.PARAMETER_JSON, files)}
                  label={`${t("new.parameter_json")} json`}
                  requiredMessage={t("new.required_attachments")}
                  clearErrors={clearErrors}
                  errors={errors}
                  register={register}
                  fileTypeAllow={[FILE_TYPE.JSON]}
                  isMultiple={uploadMultiple.parameter_json}
                  required
                  onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.PARAMETER_JSON, files, fileSelected)}
                  initialFiles={getValues(FORM_NAME.PARAMETER_JSON)}
                  checkDuplicatedFileNames
                  ignoreUploadS3
                  simulationType={SIMULATION_TYPE["6DOF"]}
                  isSubmitting={isSubmitting}
                />
                {isShowUploadMoreFilesButton(FORM_NAME.PARAMETER_JSON) && (
                  <UploadMoreFiles
                    className="mb-3"
                    onFileSelect={handleFileUpload}
                    fieldName={FORM_NAME.PARAMETER_JSON}
                    isDisabled={!!globalUploadingField}
                    fileTypeAllow={[FILE_TYPE.JSON]}
                    checkDuplicatedFileNames
                    initialFiles={getValues(FORM_NAME.PARAMETER_JSON)}
                    simulationType={SIMULATION_TYPE["6DOF"]}
                  />
                )}
              </div>

              <div>
                <UploadFileDraggable
                  classGroup={classNames({
                    "mb-0": isShowUploadMoreFilesButton(FORM_NAME.MASS_AND_SHAPE_RESPONSE),
                  })}
                  name={FORM_NAME.MASS_AND_SHAPE_RESPONSE}
                  setValue={(files) => handleFileUpload(FORM_NAME.MASS_AND_SHAPE_RESPONSE, files)}
                  label={`${t("new.mass_and_shape_response")} json (option)`}
                  clearErrors={clearErrors}
                  errors={errors}
                  register={register}
                  fileTypeAllow={[FILE_TYPE.JSON]}
                  isMultiple={uploadMultiple.mass_and_shape_response}
                  onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.MASS_AND_SHAPE_RESPONSE, files, fileSelected)}
                  initialFiles={getValues(FORM_NAME.MASS_AND_SHAPE_RESPONSE)}
                  checkDuplicatedFileNames
                  ignoreUploadS3
                  simulationType={SIMULATION_TYPE["6DOF"]}
                  isSubmitting={isSubmitting}
                />
                {isShowUploadMoreFilesButton(FORM_NAME.MASS_AND_SHAPE_RESPONSE) && (
                  <UploadMoreFiles
                    className="mb-3"
                    onFileSelect={handleFileUpload}
                    fieldName={FORM_NAME.MASS_AND_SHAPE_RESPONSE}
                    isDisabled={!!globalUploadingField}
                    fileTypeAllow={[FILE_TYPE.JSON]}
                    checkDuplicatedFileNames
                    initialFiles={getValues(FORM_NAME.MASS_AND_SHAPE_RESPONSE)}
                    simulationType={SIMULATION_TYPE["6DOF"]}
                  />
                )}
              </div>

              <div>
                <UploadFileDraggable
                  classGroup={classNames({
                    "mb-0": isShowUploadMoreFilesButton(FORM_NAME.AERO_COEFFICIENTS_ASCENT),
                  })}
                  name={FORM_NAME.AERO_COEFFICIENTS_ASCENT}
                  setValue={(files) => handleFileUpload(FORM_NAME.AERO_COEFFICIENTS_ASCENT, files)}
                  label={`${t("new.aero_coefficients_ascent")} xlsx (option)`}
                  clearErrors={clearErrors}
                  errors={errors}
                  register={register}
                  fileTypeAllow={[FILE_TYPE.XLSX]}
                  isMultiple={uploadMultiple.aero_coefficients_ascent}
                  onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.AERO_COEFFICIENTS_ASCENT, files, fileSelected)}
                  initialFiles={getValues(FORM_NAME.AERO_COEFFICIENTS_ASCENT)}
                  checkDuplicatedFileNames
                  ignoreUploadS3
                  simulationType={SIMULATION_TYPE["6DOF"]}
                  isSubmitting={isSubmitting}
                />
                {isShowUploadMoreFilesButton(FORM_NAME.AERO_COEFFICIENTS_ASCENT) && (
                  <UploadMoreFiles
                    className="mb-3"
                    onFileSelect={handleFileUpload}
                    fieldName={FORM_NAME.AERO_COEFFICIENTS_ASCENT}
                    isDisabled={!!globalUploadingField}
                    fileTypeAllow={[FILE_TYPE.XLSX]}
                    checkDuplicatedFileNames
                    initialFiles={getValues(FORM_NAME.AERO_COEFFICIENTS_ASCENT)}
                    simulationType={SIMULATION_TYPE["6DOF"]}
                  />
                )}
              </div>

              <div>
                <UploadFileDraggable
                  classGroup={classNames({
                    "mb-0": isShowUploadMoreFilesButton(FORM_NAME.AERO_COEFFICIENTS_ASCENT_2),
                  })}
                  name={FORM_NAME.AERO_COEFFICIENTS_ASCENT_2}
                  setValue={(files) => handleFileUpload(FORM_NAME.AERO_COEFFICIENTS_ASCENT_2, files)}
                  label={`${t("new.aero_coefficients_ascent_2")} xlsx (option)`}
                  clearErrors={clearErrors}
                  errors={errors}
                  register={register}
                  fileTypeAllow={[FILE_TYPE.XLSX]}
                  isMultiple={uploadMultiple.aero_coefficients_ascent_2}
                  onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.AERO_COEFFICIENTS_ASCENT_2, files, fileSelected)}
                  initialFiles={getValues(FORM_NAME.AERO_COEFFICIENTS_ASCENT_2)}
                  checkDuplicatedFileNames
                  ignoreUploadS3
                  simulationType={SIMULATION_TYPE["6DOF"]}
                  isSubmitting={isSubmitting}
                />
                {isShowUploadMoreFilesButton(FORM_NAME.AERO_COEFFICIENTS_ASCENT_2) && (
                  <UploadMoreFiles
                    className="mb-3"
                    onFileSelect={handleFileUpload}
                    fieldName={FORM_NAME.AERO_COEFFICIENTS_ASCENT_2}
                    isDisabled={!!globalUploadingField}
                    fileTypeAllow={[FILE_TYPE.XLSX]}
                    checkDuplicatedFileNames
                    initialFiles={getValues(FORM_NAME.AERO_COEFFICIENTS_ASCENT_2)}
                    simulationType={SIMULATION_TYPE["6DOF"]}
                  />
                )}
              </div>

              <div>
                <UploadFileDraggable
                  classGroup={classNames({
                    "mb-0": isShowUploadMoreFilesButton(FORM_NAME.AERO_COEFFICIENTS_DESCENT),
                  })}
                  name={FORM_NAME.AERO_COEFFICIENTS_DESCENT}
                  setValue={(files) => handleFileUpload(FORM_NAME.AERO_COEFFICIENTS_DESCENT, files)}
                  label={`${t("new.aero_coefficients_descent")} xlsx/xlsm (option)`}
                  clearErrors={clearErrors}
                  errors={errors}
                  register={register}
                  fileTypeAllow={[FILE_TYPE.XLSX, FILE_TYPE.XLSM]}
                  isMultiple={uploadMultiple.aero_coefficients_descent}
                  onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.AERO_COEFFICIENTS_DESCENT, files, fileSelected)}
                  initialFiles={getValues(FORM_NAME.AERO_COEFFICIENTS_DESCENT)}
                  checkDuplicatedFileNames
                  ignoreUploadS3
                  simulationType={SIMULATION_TYPE["6DOF"]}
                  isSubmitting={isSubmitting}
                />
                {isShowUploadMoreFilesButton(FORM_NAME.AERO_COEFFICIENTS_DESCENT) && (
                  <UploadMoreFiles
                    className="mb-3"
                    onFileSelect={handleFileUpload}
                    fieldName={FORM_NAME.AERO_COEFFICIENTS_DESCENT}
                    isDisabled={!!globalUploadingField}
                    fileTypeAllow={[FILE_TYPE.XLSX]}
                    checkDuplicatedFileNames
                    initialFiles={getValues(FORM_NAME.AERO_COEFFICIENTS_DESCENT)}
                    simulationType={SIMULATION_TYPE["6DOF"]}
                  />
                )}
              </div>
            </div>
            <div className="col-lg-6 p-a-16 important-p-y-0">
              <div>
                <UploadFileDraggable
                  classGroup={classNames({
                    "mb-0": isShowUploadMoreFilesButton(FORM_NAME.WIND_MODEL),
                  })}
                  name={FORM_NAME.WIND_MODEL}
                  setValue={(files) => handleFileUpload(FORM_NAME.WIND_MODEL, files)}
                  label={`${t("new.wind_model")} csv (option)`}
                  clearErrors={clearErrors}
                  errors={errors}
                  register={register}
                  fileTypeAllow={[FILE_TYPE.CSV]}
                  isMultiple={uploadMultiple.wind_model}
                  onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.WIND_MODEL, files, fileSelected)}
                  initialFiles={getValues(FORM_NAME.WIND_MODEL)}
                  checkDuplicatedFileNames
                  ignoreUploadS3
                  simulationType={SIMULATION_TYPE["6DOF"]}
                  isSubmitting={isSubmitting}
                />
                {isShowUploadMoreFilesButton(FORM_NAME.WIND_MODEL) && (
                  <UploadMoreFiles
                    className="mb-3"
                    onFileSelect={handleFileUpload}
                    fieldName={FORM_NAME.WIND_MODEL}
                    isDisabled={!!globalUploadingField}
                    fileTypeAllow={[FILE_TYPE.CSV]}
                    checkDuplicatedFileNames
                    initialFiles={getValues(FORM_NAME.WIND_MODEL)}
                    simulationType={SIMULATION_TYPE["6DOF"]}
                  />
                )}
              </div>

              <div>
                <UploadFileDraggable
                  classGroup={classNames({
                    "mb-0": isShowUploadMoreFilesButton(FORM_NAME.THRUST_PROFILE),
                  })}
                  name={FORM_NAME.THRUST_PROFILE}
                  setValue={(files) => handleFileUpload(FORM_NAME.THRUST_PROFILE, files)}
                  label={`${t("new.thrust_profile")} xlsx (option)`}
                  clearErrors={clearErrors}
                  errors={errors}
                  register={register}
                  fileTypeAllow={[FILE_TYPE.XLSX]}
                  isMultiple={uploadMultiple.thrust_profile}
                  onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.THRUST_PROFILE, files, fileSelected)}
                  initialFiles={getValues(FORM_NAME.THRUST_PROFILE)}
                  checkDuplicatedFileNames
                  ignoreUploadS3
                  simulationType={SIMULATION_TYPE["6DOF"]}
                  isSubmitting={isSubmitting}
                />
                {isShowUploadMoreFilesButton(FORM_NAME.THRUST_PROFILE) && (
                  <UploadMoreFiles
                    className="mb-3"
                    onFileSelect={handleFileUpload}
                    fieldName={FORM_NAME.THRUST_PROFILE}
                    isDisabled={!!globalUploadingField}
                    fileTypeAllow={[FILE_TYPE.XLSX]}
                    checkDuplicatedFileNames
                    initialFiles={getValues(FORM_NAME.THRUST_PROFILE)}
                    simulationType={SIMULATION_TYPE["6DOF"]}
                  />
                )}
              </div>
              <div className="col-md-12 mb-3">
                <Label className="position-relative">{t("new.target_mode")}</Label>
                <div className="d-flex gap-4">
                  <div className="cursor-pointer">
                    <Input
                      type="radio"
                      id="targetMode-rate"
                      className="cursor-pointer"
                      checked={targetMode === TARGET_MODE.RATE}
                      onChange={() => {
                        setValue(FORM_NAME.TARGET_MODE, TARGET_MODE.RATE);
                        setTargetMode(TARGET_MODE.RATE);
                        resetField(FORM_NAME.TARGET_ATTITUDE_ASCENT);
                        resetField(FORM_NAME.TARGET_ATTITUDE_DESCENT);

                        const updateState = updateUploadMultipleState(getValues(), uploadMultiple);
                        setUploadMultiple(updateState);
                      }}
                    />
                    <Label for="targetMode-rate" className="fw-normal m-l-4 m-t-2 cursor-pointer">
                      {t("new.target_mode_rate")} deg/s
                    </Label>
                  </div>
                  <div className="cursor-pointer">
                    <Input
                      type="radio"
                      id="targetMode-attitude"
                      className="cursor-pointer"
                      checked={targetMode === TARGET_MODE.ATTITUDE}
                      onChange={() => {
                        setValue(FORM_NAME.TARGET_MODE, TARGET_MODE.ATTITUDE);
                        setTargetMode(TARGET_MODE.ATTITUDE);
                        resetField(FORM_NAME.TARGET_ATTITUDE_RATE_ASCENT);
                        resetField(FORM_NAME.TARGET_ATTITUDE_RATE_DESCENT);

                        const updateState = updateUploadMultipleState(getValues(), uploadMultiple);
                        setUploadMultiple(updateState);
                      }}
                    />
                    <Label for="targetMode-attitude" className="fw-normal m-l-4 m-t-2 cursor-pointer">
                      {t("new.target_mode_attitude")} deg
                    </Label>
                  </div>
                </div>
              </div>
              <div
                className={classNames({
                  "d-none": targetMode !== TARGET_MODE.RATE,
                  "d-block": targetMode === TARGET_MODE.RATE,
                })}
              >
                <div>
                  <UploadFileDraggable
                    classGroup={classNames({
                      "mb-0": isShowUploadMoreFilesButton(FORM_NAME.TARGET_ATTITUDE_RATE_ASCENT),
                    })}
                    name={FORM_NAME.TARGET_ATTITUDE_RATE_ASCENT}
                    setValue={(files) => handleFileUpload(FORM_NAME.TARGET_ATTITUDE_RATE_ASCENT, files)}
                    label={`${t("new.target_attitude_rate_ascent")} csv (option)`}
                    clearErrors={clearErrors}
                    errors={errors}
                    register={register}
                    fileTypeAllow={[FILE_TYPE.CSV]}
                    isMultiple={uploadMultiple.target_attitude_rate_ascent}
                    onDeleteFile={(files, fileSelected) =>
                      handleModalFileDelete(FORM_NAME.TARGET_ATTITUDE_RATE_ASCENT, files, fileSelected)
                    }
                    initialFiles={getValues(FORM_NAME.TARGET_ATTITUDE_RATE_ASCENT)}
                    checkDuplicatedFileNames
                    ignoreUploadS3
                    simulationType={SIMULATION_TYPE["6DOF"]}
                    isSubmitting={isSubmitting}
                  />
                  {isShowUploadMoreFilesButton(FORM_NAME.TARGET_ATTITUDE_RATE_ASCENT) && (
                    <UploadMoreFiles
                      className="mb-3"
                      onFileSelect={handleFileUpload}
                      fieldName={FORM_NAME.TARGET_ATTITUDE_RATE_ASCENT}
                      isDisabled={!!globalUploadingField}
                      fileTypeAllow={[FILE_TYPE.CSV]}
                      checkDuplicatedFileNames
                      initialFiles={getValues(FORM_NAME.TARGET_ATTITUDE_RATE_ASCENT)}
                      simulationType={SIMULATION_TYPE["6DOF"]}
                    />
                  )}
                </div>
                <div>
                  <UploadFileDraggable
                    classGroup={classNames({
                      "mb-0": isShowUploadMoreFilesButton(FORM_NAME.TARGET_ATTITUDE_RATE_DESCENT),
                    })}
                    name={FORM_NAME.TARGET_ATTITUDE_RATE_DESCENT}
                    setValue={(files) => handleFileUpload(FORM_NAME.TARGET_ATTITUDE_RATE_DESCENT, files)}
                    label={`${t("new.target_attitude_rate_descent")} csv (option)`}
                    clearErrors={clearErrors}
                    errors={errors}
                    register={register}
                    fileTypeAllow={[FILE_TYPE.CSV]}
                    isMultiple={uploadMultiple.target_attitude_rate_descent}
                    onDeleteFile={(files, fileSelected) =>
                      handleModalFileDelete(FORM_NAME.TARGET_ATTITUDE_RATE_DESCENT, files, fileSelected)
                    }
                    initialFiles={getValues(FORM_NAME.TARGET_ATTITUDE_RATE_DESCENT)}
                    checkDuplicatedFileNames
                    ignoreUploadS3
                    simulationType={SIMULATION_TYPE["6DOF"]}
                    isSubmitting={isSubmitting}
                  />
                  {isShowUploadMoreFilesButton(FORM_NAME.TARGET_ATTITUDE_RATE_DESCENT) && (
                    <UploadMoreFiles
                      className="mb-3"
                      onFileSelect={handleFileUpload}
                      fieldName={FORM_NAME.TARGET_ATTITUDE_RATE_DESCENT}
                      isDisabled={!!globalUploadingField}
                      fileTypeAllow={[FILE_TYPE.CSV]}
                      checkDuplicatedFileNames
                      initialFiles={getValues(FORM_NAME.TARGET_ATTITUDE_RATE_DESCENT)}
                      simulationType={SIMULATION_TYPE["6DOF"]}
                    />
                  )}
                </div>
              </div>
              <div
                className={classNames({
                  "d-none": targetMode !== TARGET_MODE.ATTITUDE,
                  "d-block": targetMode === TARGET_MODE.ATTITUDE,
                })}
              >
                <div>
                  <UploadFileDraggable
                    classGroup={classNames({
                      "mb-0": isShowUploadMoreFilesButton(FORM_NAME.TARGET_ATTITUDE_ASCENT),
                    })}
                    name={FORM_NAME.TARGET_ATTITUDE_ASCENT}
                    setValue={(files) => handleFileUpload(FORM_NAME.TARGET_ATTITUDE_ASCENT, files)}
                    label={`${t("new.target_attitude_ascent")} csv (option)`}
                    clearErrors={clearErrors}
                    errors={errors}
                    register={register}
                    fileTypeAllow={[FILE_TYPE.CSV]}
                    isMultiple={uploadMultiple.target_attitude_ascent}
                    onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.TARGET_ATTITUDE_ASCENT, files, fileSelected)}
                    initialFiles={getValues(FORM_NAME.TARGET_ATTITUDE_ASCENT)}
                    checkDuplicatedFileNames
                    ignoreUploadS3
                    simulationType={SIMULATION_TYPE["6DOF"]}
                    isSubmitting={isSubmitting}
                  />
                  {isShowUploadMoreFilesButton(FORM_NAME.TARGET_ATTITUDE_ASCENT) && (
                    <UploadMoreFiles
                      className="mb-3"
                      onFileSelect={handleFileUpload}
                      fieldName={FORM_NAME.TARGET_ATTITUDE_ASCENT}
                      isDisabled={!!globalUploadingField}
                      fileTypeAllow={[FILE_TYPE.CSV]}
                      checkDuplicatedFileNames
                      initialFiles={getValues(FORM_NAME.TARGET_ATTITUDE_ASCENT)}
                      simulationType={SIMULATION_TYPE["6DOF"]}
                    />
                  )}
                </div>
                <div>
                  <UploadFileDraggable
                    classGroup={classNames({
                      "mb-0": isShowUploadMoreFilesButton(FORM_NAME.TARGET_ATTITUDE_DESCENT),
                    })}
                    name={FORM_NAME.TARGET_ATTITUDE_DESCENT}
                    setValue={(files) => handleFileUpload(FORM_NAME.TARGET_ATTITUDE_DESCENT, files)}
                    label={`${t("new.target_attitude_descent")} csv (option)`}
                    clearErrors={clearErrors}
                    errors={errors}
                    register={register}
                    fileTypeAllow={[FILE_TYPE.CSV]}
                    isMultiple={uploadMultiple.target_attitude_descent}
                    onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.TARGET_ATTITUDE_DESCENT, files, fileSelected)}
                    initialFiles={getValues(FORM_NAME.TARGET_ATTITUDE_DESCENT)}
                    checkDuplicatedFileNames
                    ignoreUploadS3
                    simulationType={SIMULATION_TYPE["6DOF"]}
                    isSubmitting={isSubmitting}
                  />
                  {isShowUploadMoreFilesButton(FORM_NAME.TARGET_ATTITUDE_DESCENT) && (
                    <UploadMoreFiles
                      className="mb-3"
                      onFileSelect={handleFileUpload}
                      fieldName={FORM_NAME.TARGET_ATTITUDE_DESCENT}
                      isDisabled={!!globalUploadingField}
                      fileTypeAllow={[FILE_TYPE.CSV]}
                      checkDuplicatedFileNames
                      initialFiles={getValues(FORM_NAME.TARGET_ATTITUDE_DESCENT)}
                      simulationType={SIMULATION_TYPE["6DOF"]}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </Form>
      </ModalBody>
      <ModalFooter className="justify-content-center p-t-10 p-b-20">
        <button className="btn btn-sm btn-secondary m-x-10 width-by-px-120" onClick={handleCancel}>
          {t("new.cancel")}
        </button>
        <button
          className="btn btn-sm btn-primary m-x-10 width-by-px-120"
          disabled={!!globalUploadingField || isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {t("new.register")}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default ModalUploadFile6DOF;
